import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from '@tanstack/react-router'
import { Check, Pencil, Plus, Search as SearchIcon, Trash2, Undo2, X } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import { type Absence, type AbsenceStatus, type AbsenceType, api } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { invalidateAbsenceData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import {
  AbsenceFormDialog,
  formatAbsenceDate,
  TYPE_COLOR,
  TYPE_ICON,
  TYPE_LABEL,
  workdaysBetween,
} from './absence-form-dialog'
import {
  EmptyTable,
  MiniPager,
  Pager,
  SortableHeader,
  TableToolbar,
  useEmbeddedPageSize,
} from './data-table'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select } from './ui/select'
import { SidePanel } from './ui/side-panel'

// Standalone /requests route + Sidebar entry were removed — all request
// (absence) functionality lives in the calendar Erfassen-Panel + the
// CalendarInfoPanel "Anträge" tab + the chat absence card. This file
// keeps the page body exported so the embedded view still works.

// ── Status badges ──────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  AbsenceStatus,
  { label: string; bg: string; border: string; text: string }
> = {
  pending: {
    label: 'Ausstehend',
    bg: 'bg-warning/15',
    border: 'border-warning/40',
    text: 'text-warning',
  },
  approved: {
    label: 'Genehmigt',
    bg: 'bg-success/15',
    border: 'border-success/40',
    text: 'text-success',
  },
  rejected: {
    label: 'Abgelehnt',
    bg: 'bg-danger/15',
    border: 'border-danger/40',
    text: 'text-danger',
  },
  withdrawn: {
    label: 'Zurückgezogen',
    bg: 'bg-bg-overlay/40',
    border: 'border-border-default',
    text: 'text-text-tertiary',
  },
  cancelled: {
    label: 'Storniert',
    bg: 'bg-bg-overlay/40',
    border: 'border-border-default',
    text: 'text-text-tertiary',
  },
}

function StatusBadge({ status }: { status: AbsenceStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.approved
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text}`}
    >
      {cfg.label}
    </span>
  )
}

// ── Embedded helpers ────────────────────────────────────────────────
// Search input + mini pager used in narrow CalendarInfoPanel mode
// (~380-420px). Kept here rather than promoted to data-table.tsx to
// avoid churning the shared toolbar API for one consumer.

function EmbeddedSearch({
  search,
  onSearch,
  filteredRows,
  totalRows,
  rightSlot,
}: {
  search: string
  onSearch: (q: string) => void
  filteredRows: number
  totalRows: number
  rightSlot?: ReactNode
}) {
  return (
    <div className="space-y-2 py-2">
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Suchen…"
          className="w-full h-9 pl-9 pr-9 rounded-lg border border-border-default bg-bg-overlay/40 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent/60 focus:bg-bg-overlay focus:ring-2 focus:ring-accent/20"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearch('')}
            aria-label="Suche zurücksetzen"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-tertiary hover:text-accent rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {rightSlot && <div className="flex flex-wrap gap-2">{rightSlot}</div>}
      <p className="text-[11px] text-text-tertiary tabular-nums">
        {search.trim() ? `${filteredRows} von ${totalRows}` : `${totalRows} Einträge`}
      </p>
    </div>
  )
}

// ── Tabs ────────────────────────────────────────────────────────────

type Tab = 'mine' | 'approvals' | 'all'

export function RequestsPageBody({ embedded = false }: { embedded?: boolean } = {}) {
  const [tab, setTab] = useState<Tab>('mine')
  const [createOpen, setCreateOpen] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  // Detect if user has subordinates (is someone's supervisor) or is admin
  const { data: pendingData } = useQuery({
    queryKey: ['absences', 'pending'],
    queryFn: () => api.listPendingAbsences(),
    enabled: !!meData?.user,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const user = meData.user
  const isAdmin = user.role === 'admin'
  const pendingCount = pendingData?.absences?.length ?? 0
  // Show approvals tab if user has pending items (means they're a supervisor or admin)
  const showApprovals = pendingCount > 0 || isAdmin

  const tabs: { id: Tab; label: string; badge?: number }[] = [
    { id: 'mine', label: 'Meine Anträge' },
  ]
  if (showApprovals) {
    tabs.push({
      id: 'approvals',
      label: 'Genehmigungen',
      badge: pendingCount > 0 ? pendingCount : undefined,
    })
  }
  if (isAdmin) {
    tabs.push({ id: 'all', label: 'Alle Anträge' })
  }

  return (
    <div className={cn(embedded ? 'space-y-3' : 'space-y-4 animate-fade-in')}>
      {/* Header row */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 border-b border-border-subtle',
          embedded ? 'pb-2' : 'lg:flex-nowrap pb-3',
        )}
      >
        {!embedded && (
          <h1 className="text-lg font-medium tracking-tight text-text-primary shrink-0">Anträge</h1>
        )}
        <div className="flex items-center gap-1 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs sm:text-sm transition-colors whitespace-nowrap',
                tab === t.id
                  ? 'bg-accent/15 text-accent font-medium'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-overlay/40',
              )}
            >
              {t.label}
              {t.badge != null && (
                <span className="inline-flex items-center justify-center rounded-full bg-warning/15 border border-warning/40 text-warning text-2xs font-medium min-w-[18px] h-[18px] px-1">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        {!embedded && <div className="hidden lg:block flex-1" />}
        {/* Embedded mode hides the create button — Erfassen-Panel covers it */}
        {!embedded && tab === 'mine' && (
          <Button onClick={() => setCreateOpen(true)} size="sm" className="shrink-0">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline"> Neuer Antrag</span>
          </Button>
        )}
      </div>

      {/* Tab content */}
      {tab === 'mine' && (
        <MyRequestsTab userId={user.id} embedded={embedded} onCreate={() => setCreateOpen(true)} />
      )}
      {tab === 'approvals' && <ApprovalsTab embedded={embedded} />}
      {tab === 'all' && <AllRequestsTab embedded={embedded} />}

      {/* Create dialog */}
      <AbsenceFormDialog open={createOpen} absence={null} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

// ── Tab 1: Meine Anträge ────────────────────────────────────────────

function MyRequestsTab(_props: { userId: string; embedded?: boolean; onCreate?: () => void }) {
  const { embedded = false, onCreate } = _props
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['absences'],
    queryFn: () => api.listAbsences(),
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
  })

  const absences = useMemo(() => {
    const raw = data?.absences ?? []
    // Sort newest first by createdAt
    return [...raw].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
  }, [data])

  const userMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of usersData?.users ?? []) map.set(u.id, u.name)
    return map
  }, [usersData])

  const table = useDataTable<Absence>(
    absences,
    {
      type: (r) => TYPE_LABEL[r.type],
      fromDate: 'fromDate',
      status: 'status',
      createdAt: 'createdAt',
    },
    {
      searchableFields: [(r) => TYPE_LABEL[r.type], 'notes', 'reviewNote'],
      defaultSortKey: 'createdAt',
      defaultSortDir: 'desc',
      defaultPageSize: 25,
      storageKey: 'requests-mine',
    },
  )

  const withdrawMutation = useMutation({
    mutationFn: (id: string) => api.withdrawAbsence(id),
    onSuccess: () => {
      invalidateAbsenceData(queryClient)
      notify.success('Antrag zurückgezogen')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onWithdraw = async (absence: Absence) => {
    const ok = await confirm({
      title: 'Antrag zurückziehen?',
      description: `${TYPE_LABEL[absence.type]} ${formatAbsenceDate(absence.fromDate)} – ${formatAbsenceDate(absence.toDate)}`,
      variant: 'danger',
      confirmLabel: 'Zurückziehen',
    })
    if (ok) withdrawMutation.mutate(absence.id)
  }

  if (isLoading) {
    return <p className="text-text-secondary py-8 text-center">Lade Anträge …</p>
  }

  if (embedded) {
    return (
      <div>
        <EmbeddedSearch
          search={table.search}
          onSearch={table.setSearch}
          filteredRows={table.filteredRows}
          totalRows={table.totalRows}
        />
        {table.rows.length === 0 ? (
          // Ehrlicher Empty-State: das mobile Panel hat oben rechts nur das
          // Schließen-X — der CTA muss deshalb direkt hier stehen.
          <EmptyTable
            message="Keine Anträge vorhanden."
            action={
              onCreate && (
                <Button size="sm" onClick={onCreate}>
                  <Plus className="h-4 w-4" /> Antrag stellen
                </Button>
              )
            }
          />
        ) : (
          <ul className="space-y-2">
            {table.rows.map((a) => {
              const Icon = TYPE_ICON[a.type]
              const color = TYPE_COLOR[a.type]
              const days = workdaysBetween(a.fromDate, a.toDate, a.firstDayHalf, a.lastDayHalf)
              const isPending = a.status === 'pending'
              return (
                <li
                  key={a.id}
                  className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-3 space-y-2"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: `${color}20`, color }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-medium text-sm text-text-primary truncate flex-1">
                      {TYPE_LABEL[a.type]}
                    </span>
                    <StatusBadge status={a.status ?? 'approved'} />
                  </div>
                  <div className="text-xs text-text-secondary">
                    {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)}
                    <span className="ml-1 text-text-tertiary">({days} Werktage)</span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-text-tertiary">
                    <span title="Eingereicht am">
                      <span className="opacity-60">Antrag:</span> {formatAbsenceDate(a.createdAt)}
                    </span>
                    <span title="Bearbeitet von">
                      <span className="opacity-60">Bearbeiter:</span>{' '}
                      {a.reviewedBy ? (userMap.get(a.reviewedBy) ?? a.reviewerName ?? '-') : '–'}
                    </span>
                  </div>
                  {(a.reviewNote ?? a.notes) && (
                    <p className="text-xs italic text-text-tertiary line-clamp-2 break-words">
                      {a.reviewNote ?? a.notes}
                    </p>
                  )}
                  {isPending && (
                    <div className="flex">
                      <button
                        type="button"
                        onClick={() => onWithdraw(a)}
                        disabled={withdrawMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-md bg-danger/10 border border-danger/30 text-danger px-2.5 py-1 text-[11px] font-medium hover:bg-danger/20 transition-colors"
                        title="Zurückziehen"
                      >
                        <Undo2 className="h-3 w-3" /> Zurückziehen
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        <MiniPager
          page={table.page}
          totalPages={table.totalPages}
          onPage={table.setPage}
          pageSize={table.pageSize}
          onPageSize={(n) => {
            table.setPageSize(n)
            table.setPage(1)
          }}
        />
      </div>
    )
  }

  return (
    <div>
      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
      />

      <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-bg-elevated/40">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle bg-bg-elevated/60">
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Typ"
                  sortKey="type"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Zeitraum"
                  sortKey="fromDate"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Eingereicht am"
                  sortKey="createdAt"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                  Bearbeitet von
                </span>
              </th>
              <th className="px-4 py-2.5 text-left">
                <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                  Notiz
                </span>
              </th>
              <th className="px-4 py-2.5 w-24" />
            </tr>
          </thead>
          <tbody>
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyTable
                    message="Keine Anträge vorhanden."
                    action={
                      onCreate && (
                        <Button size="sm" onClick={onCreate}>
                          <Plus className="h-4 w-4" /> Antrag stellen
                        </Button>
                      )
                    }
                  />
                </td>
              </tr>
            ) : (
              table.rows.map((a) => {
                const Icon = TYPE_ICON[a.type]
                const color = TYPE_COLOR[a.type]
                const days = workdaysBetween(a.fromDate, a.toDate, a.firstDayHalf, a.lastDayHalf)
                const isPending = a.status === 'pending'
                return (
                  <tr
                    key={a.id}
                    className="border-b border-border-subtle last:border-0 hover:bg-bg-overlay/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
                          style={{ backgroundColor: `${color}20`, color }}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="font-medium text-text-primary">{TYPE_LABEL[a.type]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                      {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)}
                      <span className="ml-1.5 text-xs text-text-tertiary">({days} Werktage)</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={a.status ?? 'approved'} />
                    </td>
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                      {formatAbsenceDate(a.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {a.reviewedBy ? (userMap.get(a.reviewedBy) ?? a.reviewerName ?? '-') : '-'}
                    </td>
                    <td className="px-4 py-3 text-text-tertiary text-xs max-w-[200px] truncate italic">
                      {a.reviewNote ?? a.notes ?? '-'}
                    </td>
                    <td className="px-4 py-3">
                      {isPending && (
                        <button
                          type="button"
                          onClick={() => onWithdraw(a)}
                          disabled={withdrawMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md bg-danger/10 border border-danger/30 text-danger px-2.5 py-1 text-xs font-medium hover:bg-danger/20 transition-colors opacity-0 group-hover:opacity-100 touch:opacity-100"
                          style={{ opacity: 1 }}
                          title="Zurückziehen"
                        >
                          <Undo2 className="h-3 w-3" /> Zurückziehen
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />
    </div>
  )
}

// ── Tab 2: Genehmigungen (supervisor view) ──────────────────────────

function ApprovalsTab({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient()
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [pendingPage, setPendingPage] = useState(1)
  const [recentPage, setRecentPage] = useState(1)
  const [pendingPageSize, setPendingPageSize] = useEmbeddedPageSize('requests-pending', 10)
  const [recentPageSize, setRecentPageSize] = useEmbeddedPageSize('requests-recent', 10)

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['absences', 'pending'],
    queryFn: () => api.listPendingAbsences(),
  })

  const { data: recentData, isLoading: recentLoading } = useQuery({
    queryKey: ['absences', 'recent-reviews'],
    queryFn: () => api.listRecentReviewAbsences(),
  })

  const reviewMutation = useMutation({
    mutationFn: ({
      id,
      status,
      note,
    }: {
      id: string
      status: 'approved' | 'rejected'
      note?: string
    }) => api.reviewAbsence(id, status, note),
    onSuccess: (_data, variables) => {
      invalidateAbsenceData(queryClient)
      notify.success(
        variables.status === 'approved' ? 'Abwesenheit genehmigt' : 'Abwesenheit abgelehnt',
      )
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const cancellationReview = useMutation({
    mutationFn: async ({
      id,
      action,
      note,
    }: {
      id: string
      action: 'approve' | 'reject'
      note?: string
    }) => {
      if (action === 'approve') {
        await api.approveAbsenceCancellation(id)
      } else {
        await api.rejectAbsenceCancellation(id, note)
      }
    },
    onSuccess: (_d, v) => {
      invalidateAbsenceData(queryClient)
      notify.success(v.action === 'approve' ? 'Stornierung genehmigt' : 'Stornierung abgelehnt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const pendingAbsencesAll = pendingData?.absences ?? []
  const recentAbsencesAll = (recentData?.absences ?? []).filter((a) => a.status !== 'pending')

  const pendingTotalPages = embedded
    ? Math.max(1, Math.ceil(pendingAbsencesAll.length / pendingPageSize))
    : 1
  const recentTotalPages = embedded
    ? Math.max(1, Math.ceil(recentAbsencesAll.length / recentPageSize))
    : 1
  const pendingAbsences = embedded
    ? pendingAbsencesAll.slice((pendingPage - 1) * pendingPageSize, pendingPage * pendingPageSize)
    : pendingAbsencesAll
  const recentAbsences = embedded
    ? recentAbsencesAll.slice((recentPage - 1) * recentPageSize, recentPage * recentPageSize)
    : recentAbsencesAll

  const isLoading = pendingLoading || recentLoading

  if (isLoading) {
    return <p className="text-text-secondary py-8 text-center">Lade Genehmigungen …</p>
  }

  return (
    <div className={cn(embedded ? 'space-y-4' : 'space-y-6')}>
      {/* Pending section */}
      <section>
        <h2
          className={cn(
            'text-text-primary mb-3 flex items-center gap-2 font-medium',
            embedded ? 'text-sm' : 'text-base',
          )}
        >
          Ausstehende Anträge
          {pendingAbsences.length > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-warning/15 border border-warning/40 text-warning text-2xs font-medium min-w-[20px] h-5 px-1.5">
              {pendingAbsences.length}
            </span>
          )}
        </h2>

        {pendingAbsences.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-12 text-center">
            <p className="text-text-secondary">Keine ausstehenden Anträge.</p>
          </div>
        ) : (
          <ul
            className={cn(
              'overflow-hidden rounded-2xl border border-warning/30 bg-warning/[0.03] divide-y divide-border-subtle',
            )}
          >
            {pendingAbsences.map((a) => {
              const Icon = TYPE_ICON[a.type]
              const color = TYPE_COLOR[a.type]
              const days = workdaysBetween(a.fromDate, a.toDate, a.firstDayHalf, a.lastDayHalf)
              return (
                <li
                  key={a.id}
                  className={cn(
                    'flex gap-3 flex-wrap',
                    embedded
                      ? 'px-3 py-3 flex-col items-stretch'
                      : 'px-5 py-3.5 items-center gap-4',
                  )}
                >
                  <div className={cn('flex gap-3', embedded ? 'items-start' : 'items-center')}>
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: `${color}20`, color }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text-primary">
                        {a.userName ?? 'Mitarbeiter'} — {TYPE_LABEL[a.type]}
                        {a.cancellationRequested && (
                          <span className="ml-2 inline-block text-2xs uppercase tracking-wider text-warning px-2 py-0.5 rounded bg-warning/10">
                            Stornierungs-Antrag
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)} · {days}{' '}
                        Werktage
                      </p>
                      <p className="text-[11px] text-text-tertiary/80 mt-0.5">
                        <span className="opacity-60">Antrag:</span> {formatAbsenceDate(a.createdAt)}
                      </p>
                      {a.cancellationRequested && a.cancellationReason && (
                        <p className="mt-0.5 text-xs text-warning break-words">
                          Storno-Grund: {a.cancellationReason}
                        </p>
                      )}
                      {a.notes && (
                        <p
                          className={cn(
                            'mt-0.5 text-xs text-text-tertiary italic',
                            embedded ? 'break-words line-clamp-2' : 'truncate',
                          )}
                        >
                          {a.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div
                    className={cn(
                      'flex shrink-0',
                      embedded ? 'flex-col items-stretch gap-2' : 'items-center gap-2 flex-wrap',
                    )}
                  >
                    <input
                      type="text"
                      placeholder="Notiz (optional)"
                      value={reviewNotes[a.id] ?? ''}
                      onChange={(e) =>
                        setReviewNotes((prev) => ({ ...prev, [a.id]: e.target.value }))
                      }
                      className={cn(
                        'text-xs rounded-md border border-border-default bg-bg-elevated px-2 py-1.5 placeholder:text-text-tertiary/60 focus:outline-none focus:ring-1 focus:ring-accent',
                        embedded ? 'w-full' : 'w-40',
                      )}
                    />
                    <div className={cn('flex gap-2', embedded && 'w-full')}>
                      {a.cancellationRequested ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              cancellationReview.mutate({ id: a.id, action: 'approve' })
                            }
                            disabled={cancellationReview.isPending}
                            className={cn(
                              'flex items-center justify-center gap-1 rounded-md bg-success/15 border border-success/40 text-success px-3 py-1.5 text-xs font-medium hover:bg-success/25 transition-colors touch:opacity-100',
                              embedded && 'flex-1',
                            )}
                            title="Stornierung genehmigen"
                          >
                            <Check className="h-3.5 w-3.5" /> Storno OK
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              cancellationReview.mutate({
                                id: a.id,
                                action: 'reject',
                                note: reviewNotes[a.id] || undefined,
                              })
                            }
                            disabled={cancellationReview.isPending}
                            className={cn(
                              'flex items-center justify-center gap-1 rounded-md bg-danger/15 border border-danger/40 text-danger px-3 py-1.5 text-xs font-medium hover:bg-danger/25 transition-colors touch:opacity-100',
                              embedded && 'flex-1',
                            )}
                            title="Stornierung ablehnen"
                          >
                            <X className="h-3.5 w-3.5" /> Storno ablehnen
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              reviewMutation.mutate({
                                id: a.id,
                                status: 'approved',
                                note: reviewNotes[a.id] || undefined,
                              })
                            }
                            disabled={reviewMutation.isPending}
                            className={cn(
                              'flex items-center justify-center gap-1 rounded-md bg-success/15 border border-success/40 text-success px-3 py-1.5 text-xs font-medium hover:bg-success/25 transition-colors touch:opacity-100',
                              embedded && 'flex-1',
                            )}
                            title="Genehmigen"
                          >
                            <Check className="h-3.5 w-3.5" /> Genehmigen
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              reviewMutation.mutate({
                                id: a.id,
                                status: 'rejected',
                                note: reviewNotes[a.id] || undefined,
                              })
                            }
                            disabled={reviewMutation.isPending}
                            className={cn(
                              'flex items-center justify-center gap-1 rounded-md bg-danger/15 border border-danger/40 text-danger px-3 py-1.5 text-xs font-medium hover:bg-danger/25 transition-colors touch:opacity-100',
                              embedded && 'flex-1',
                            )}
                            title="Ablehnen"
                          >
                            <X className="h-3.5 w-3.5" /> Ablehnen
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {embedded && (
          <MiniPager
            page={pendingPage}
            totalPages={pendingTotalPages}
            onPage={setPendingPage}
            pageSize={pendingPageSize}
            onPageSize={(n) => {
              setPendingPageSize(n)
              setPendingPage(1)
            }}
          />
        )}
      </section>

      {/* Recent reviewed section */}
      {(recentAbsences.length > 0 || (embedded && recentAbsencesAll.length > 0)) && (
        <section>
          <h2
            className={cn(
              'text-text-secondary mb-3',
              embedded ? 'text-sm font-medium' : 'text-base font-medium',
            )}
          >
            Kürzlich bearbeitet (letzte 30 Tage)
          </h2>
          {embedded ? (
            <ul className="space-y-2">
              {recentAbsences.map((a) => (
                <li
                  key={a.id}
                  className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-3 space-y-1.5"
                >
                  <div className="flex items-start gap-2">
                    <p className="text-sm font-medium text-text-primary truncate flex-1">
                      {a.userName ?? '-'}
                    </p>
                    <StatusBadge status={a.status ?? 'approved'} />
                  </div>
                  <p className="text-xs text-text-secondary">
                    {TYPE_LABEL[a.type]} · {formatAbsenceDate(a.fromDate)} –{' '}
                    {formatAbsenceDate(a.toDate)}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-text-tertiary">
                    <span>
                      <span className="opacity-60">Antrag:</span> {formatAbsenceDate(a.createdAt)}
                    </span>
                    <span>
                      <span className="opacity-60">Bearbeiter:</span>{' '}
                      {a.reviewedBy ? (a.reviewerName ?? '-') : '–'}
                    </span>
                  </div>
                  {a.reviewNote && (
                    <p className="text-xs italic text-text-tertiary line-clamp-2 break-words">
                      {a.reviewNote}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-bg-elevated/40">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle bg-bg-elevated/60">
                    <th className="px-4 py-2.5 text-left">
                      <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                        Mitarbeiter
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-left">
                      <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                        Typ
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-left">
                      <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                        Zeitraum
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-left">
                      <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                        Status
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-left">
                      <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                        Notiz
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentAbsences.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b border-border-subtle last:border-0 hover:bg-bg-overlay/40 transition-colors"
                    >
                      <td
                        className="px-4 py-3 font-medium text-text-primary max-w-[14rem] truncate"
                        title={a.userName ?? undefined}
                      >
                        {a.userName ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{TYPE_LABEL[a.type]}</td>
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                        {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={a.status ?? 'approved'} />
                      </td>
                      <td className="px-4 py-3 text-text-tertiary text-xs italic truncate max-w-[200px]">
                        {a.reviewNote ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {embedded && (
            <MiniPager
              page={recentPage}
              totalPages={recentTotalPages}
              onPage={setRecentPage}
              pageSize={recentPageSize}
              onPageSize={(n) => {
                setRecentPageSize(n)
                setRecentPage(1)
              }}
            />
          )}
        </section>
      )}
    </div>
  )
}

// ── Tab 3: Alle Anträge (admin only) ────────────────────────────────

function AllRequestsTab({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient()
  const [editAbsence, setEditAbsence] = useState<Absence | null>(null)
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['absences', 'all'],
    queryFn: () => api.listAllAbsences(),
  })

  const allAbsences = useMemo(() => {
    let list = data?.absences ?? []
    if (statusFilter) list = list.filter((a) => a.status === statusFilter)
    if (typeFilter) list = list.filter((a) => a.type === typeFilter)
    return list
  }, [data, statusFilter, typeFilter])

  const table = useDataTable<Absence>(
    allAbsences,
    {
      userName: 'userName',
      type: (r) => TYPE_LABEL[r.type],
      fromDate: 'fromDate',
      status: 'status',
      createdAt: 'createdAt',
    },
    {
      searchableFields: ['userName', 'userEmail', (r) => TYPE_LABEL[r.type], 'notes', 'reviewNote'],
      defaultSortKey: 'createdAt',
      defaultSortDir: 'desc',
      defaultPageSize: 25,
      storageKey: 'requests-all',
    },
  )

  const reviewMutation = useMutation({
    mutationFn: ({
      id,
      status,
      note,
    }: {
      id: string
      status: 'approved' | 'rejected'
      note?: string
    }) => api.reviewAbsence(id, status, note),
    onSuccess: (_data, variables) => {
      invalidateAbsenceData(queryClient)
      notify.success(
        variables.status === 'approved' ? 'Abwesenheit genehmigt' : 'Abwesenheit abgelehnt',
      )
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAbsence(id),
    onSuccess: () => {
      invalidateAbsenceData(queryClient)
      notify.success('Abwesenheit gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onDelete = async (absence: Absence) => {
    const ok = await confirm({
      title: 'Abwesenheit löschen?',
      description: `${absence.userName ?? 'Mitarbeiter'}: ${TYPE_LABEL[absence.type]} ${formatAbsenceDate(absence.fromDate)} – ${formatAbsenceDate(absence.toDate)}`,
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) deleteMutation.mutate(absence.id)
  }

  if (isLoading) {
    return <p className="text-text-secondary py-8 text-center">Lade alle Anträge …</p>
  }

  if (embedded) {
    return (
      <div>
        <EmbeddedSearch
          search={table.search}
          onSearch={table.setSearch}
          filteredRows={table.filteredRows}
          totalRows={table.totalRows}
          rightSlot={
            <>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 flex-1 min-w-0 text-xs"
              >
                <option value="">Alle Status</option>
                <option value="pending">Ausstehend</option>
                <option value="approved">Genehmigt</option>
                <option value="rejected">Abgelehnt</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="h-8 flex-1 min-w-0 text-xs"
              >
                <option value="">Alle Typen</option>
                <option value="vacation">Urlaub</option>
                <option value="sick">Krank</option>
                <option value="training">Fortbildung</option>
                <option value="other">Sonstiges</option>
              </Select>
            </>
          }
        />
        {table.rows.length === 0 ? (
          <EmptyTable message="Keine Anträge gefunden." />
        ) : (
          <ul className="space-y-2">
            {table.rows.map((a) => {
              const Icon = TYPE_ICON[a.type]
              const color = TYPE_COLOR[a.type]
              const days = workdaysBetween(a.fromDate, a.toDate, a.firstDayHalf, a.lastDayHalf)
              const isPending = a.status === 'pending'
              return (
                <li
                  key={a.id}
                  className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-3 space-y-2"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: `${color}20`, color }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {a.userName ?? '-'}
                      </p>
                      <p className="text-[11px] text-text-tertiary truncate">
                        {TYPE_LABEL[a.type]}
                      </p>
                    </div>
                    <StatusBadge status={a.status ?? 'approved'} />
                  </div>
                  <div className="text-xs text-text-secondary">
                    {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)}
                    <span className="ml-1 text-text-tertiary">({days}d)</span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-text-tertiary">
                    <span title="Eingereicht am">
                      <span className="opacity-60">Antrag:</span> {formatAbsenceDate(a.createdAt)}
                    </span>
                    <span title="Bearbeitet von">
                      <span className="opacity-60">Bearbeiter:</span>{' '}
                      {a.reviewedBy ? (a.reviewerName ?? '-') : '–'}
                    </span>
                  </div>
                  {(a.reviewNote ?? a.notes) && (
                    <p className="text-xs italic text-text-tertiary line-clamp-2 break-words">
                      {a.reviewNote ?? a.notes}
                    </p>
                  )}
                  <div className="flex items-center gap-1 pt-1 border-t border-border-subtle/60">
                    {isPending && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            reviewMutation.mutate({
                              id: a.id,
                              status: 'approved',
                              note: reviewNotes[a.id] || undefined,
                            })
                          }
                          disabled={reviewMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-success hover:bg-success/15 transition-colors text-[11px] font-medium"
                          title="Genehmigen"
                        >
                          <Check className="h-3.5 w-3.5" />
                          OK
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            reviewMutation.mutate({
                              id: a.id,
                              status: 'rejected',
                              note: reviewNotes[a.id] || undefined,
                            })
                          }
                          disabled={reviewMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-danger hover:bg-danger/15 transition-colors text-[11px] font-medium"
                          title="Ablehnen"
                        >
                          <X className="h-3.5 w-3.5" />
                          Nein
                        </button>
                      </>
                    )}
                    <div className="flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditAbsence(a)}
                      aria-label="Bearbeiten"
                      title="Bearbeiten"
                      className="text-text-tertiary hover:text-accent"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(a)}
                      disabled={deleteMutation.isPending}
                      aria-label="Löschen"
                      title="Löschen"
                      className="text-text-tertiary hover:text-danger"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        <MiniPager
          page={table.page}
          totalPages={table.totalPages}
          onPage={table.setPage}
          pageSize={table.pageSize}
          onPageSize={(n) => {
            table.setPageSize(n)
            table.setPage(1)
          }}
        />
        <AdminEditPanel absence={editAbsence} onClose={() => setEditAbsence(null)} />
      </div>
    )
  }

  return (
    <div>
      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
        rightSlot={
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-8 w-32 text-xs"
            >
              <option value="">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="approved">Genehmigt</option>
              <option value="rejected">Abgelehnt</option>
            </Select>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-8 w-32 text-xs"
            >
              <option value="">Alle Typen</option>
              <option value="vacation">Urlaub</option>
              <option value="sick">Krank</option>
              <option value="training">Fortbildung</option>
              <option value="other">Sonstiges</option>
            </Select>
          </div>
        }
      />

      <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-bg-elevated/40">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle bg-bg-elevated/60">
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Mitarbeiter"
                  sortKey="userName"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Typ"
                  sortKey="type"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Zeitraum"
                  sortKey="fromDate"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <SortableHeader
                  label="Eingereicht"
                  sortKey="createdAt"
                  currentKey={table.sortKey}
                  currentDir={table.sortDir}
                  onSort={table.toggleSort}
                />
              </th>
              <th className="px-4 py-2.5 text-left">
                <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                  Notiz
                </span>
              </th>
              <th className="px-4 py-2.5 w-48" />
            </tr>
          </thead>
          <tbody>
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyTable message="Keine Anträge gefunden." />
                </td>
              </tr>
            ) : (
              table.rows.map((a) => {
                const Icon = TYPE_ICON[a.type]
                const color = TYPE_COLOR[a.type]
                const days = workdaysBetween(a.fromDate, a.toDate, a.firstDayHalf, a.lastDayHalf)
                const isPending = a.status === 'pending'
                return (
                  <tr
                    key={a.id}
                    className="group border-b border-border-subtle last:border-0 hover:bg-bg-overlay/40 transition-colors"
                  >
                    <td className="px-4 py-3 max-w-[14rem]">
                      <div className="min-w-0">
                        <p
                          className="font-medium text-text-primary truncate"
                          title={a.userName ?? undefined}
                        >
                          {a.userName ?? '-'}
                        </p>
                        {a.userEmail && (
                          <p className="text-2xs text-text-tertiary truncate" title={a.userEmail}>
                            {a.userEmail}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                          style={{ backgroundColor: `${color}20`, color }}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-text-secondary">{TYPE_LABEL[a.type]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                      {formatAbsenceDate(a.fromDate)} – {formatAbsenceDate(a.toDate)}
                      <span className="ml-1 text-xs text-text-tertiary">({days}d)</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={a.status ?? 'approved'} />
                    </td>
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap text-xs">
                      {formatAbsenceDate(a.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-text-tertiary text-xs italic truncate max-w-[180px]">
                      {a.reviewNote ?? a.notes ?? '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
                        {isPending && (
                          <>
                            <input
                              type="text"
                              placeholder="Notiz"
                              value={reviewNotes[a.id] ?? ''}
                              onChange={(e) =>
                                setReviewNotes((prev) => ({
                                  ...prev,
                                  [a.id]: e.target.value,
                                }))
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="text-[11px] rounded border border-border-default bg-bg-elevated px-1.5 py-1 w-24 placeholder:text-text-tertiary/60 focus:outline-none focus:ring-1 focus:ring-accent"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                reviewMutation.mutate({
                                  id: a.id,
                                  status: 'approved',
                                  note: reviewNotes[a.id] || undefined,
                                })
                              }
                              disabled={reviewMutation.isPending}
                              className="p-1.5 rounded-md text-success hover:bg-success/15 transition-colors"
                              title="Genehmigen"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                reviewMutation.mutate({
                                  id: a.id,
                                  status: 'rejected',
                                  note: reviewNotes[a.id] || undefined,
                                })
                              }
                              disabled={reviewMutation.isPending}
                              className="p-1.5 rounded-md text-danger hover:bg-danger/15 transition-colors"
                              title="Ablehnen"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditAbsence(a)}
                          aria-label="Bearbeiten"
                          title="Bearbeiten"
                          className="text-text-tertiary hover:text-accent"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(a)}
                          disabled={deleteMutation.isPending}
                          aria-label="Löschen"
                          title="Löschen"
                          className="text-text-tertiary hover:text-danger"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      {/* Admin edit panel */}
      <AdminEditPanel absence={editAbsence} onClose={() => setEditAbsence(null)} />
    </div>
  )
}

// ── Admin Edit SidePanel ────────────────────────────────────────────

function AdminEditPanel({ absence, onClose }: { absence: Absence | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const open = !!absence
  const [type, setType] = useState<AbsenceType>('vacation')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (absence) {
      setType(absence.type)
      setFromDate(absence.fromDate.slice(0, 10))
      setToDate(absence.toDate.slice(0, 10))
      setNotes(absence.notes ?? '')
    }
  }, [absence])

  const update = useMutation({
    mutationFn: () =>
      api.updateAbsence(absence?.id ?? '', {
        type,
        fromDate,
        toDate,
        notes: notes || null,
      }),
    onSuccess: () => {
      invalidateAbsenceData(queryClient)
      notify.success('Abwesenheit aktualisiert')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (new Date(toDate) < new Date(fromDate)) {
      notify.error('Bis-Datum muss nach Von-Datum liegen')
      return
    }
    update.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Abwesenheit bearbeiten"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={update.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        {absence && (
          <div className="rounded-lg border border-border-subtle bg-bg-overlay/40 px-4 py-3">
            <p className="text-xs text-text-tertiary">Mitarbeiter</p>
            <p className="text-sm font-medium text-text-primary">{absence.userName ?? '-'}</p>
            {absence.userEmail && <p className="text-xs text-text-tertiary">{absence.userEmail}</p>}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="admin-abs-type">Typ</Label>
          <Select
            id="admin-abs-type"
            value={type}
            onChange={(e) => setType(e.target.value as AbsenceType)}
          >
            <option value="vacation">Urlaub</option>
            <option value="sick">Krank</option>
            <option value="training">Fortbildung</option>
            <option value="other">Sonstiges</option>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="admin-abs-from">Von</Label>
            <Input
              id="admin-abs-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-abs-to">Bis</Label>
            <Input
              id="admin-abs-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-abs-notes">Notiz</Label>
          <Input
            id="admin-abs-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notiz (optional)"
          />
        </div>
        {absence && (
          <div className="rounded-lg border border-border-subtle bg-bg-overlay/40 px-4 py-3 space-y-1">
            <p className="text-xs text-text-tertiary">
              Status: <StatusBadge status={absence.status ?? 'approved'} />
            </p>
            {absence.reviewedBy && (
              <p className="text-xs text-text-tertiary">
                Bearbeitet von: {absence.reviewerName ?? absence.reviewedBy}
              </p>
            )}
            {absence.reviewNote && (
              <p className="text-xs text-text-tertiary italic">Anmerkung: {absence.reviewNote}</p>
            )}
          </div>
        )}
      </form>
    </SidePanel>
  )
}
